import { Directionality } from '@angular/cdk/bidi';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Platform } from '@angular/cdk/platform';
import {
  ANIMATION_MODULE_TYPE,
  AfterContentChecked,
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  Inject,
  Input,
  isDevMode,
  NgZone,
  OnDestroy,
  Optional,
  QueryList,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Subject, from, merge } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';

import { FormFieldErrorDirective } from './error.directive';
import { formFieldAnimations } from './form-field-animations';
import {
  APP_FORM_FIELD_DEFAULT_OPTIONS,
  AppFormFieldDefaultOptions,
  FormFieldAppearance,
  FormFieldColor,
  FormFieldControl,
  FormFieldLabelPosition,
} from './form-field.types';
import { FormFieldHintDirective } from './hint.directive';
import { FormFieldLabelDirective } from './label.directive';
import {
  FormFieldPrefixDirective,
  FormFieldSuffixDirective,
} from './prefix-suffix.directive';

type FloatLabelType = 'always' | 'never' | 'auto';
type NgControlStatus =
  | 'untouched'
  | 'touched'
  | 'pristine'
  | 'dirty'
  | 'valid'
  | 'invalid'
  | 'pending';

let nextUniqueId = 0;
const outlineGapPadding = 5;
const floatingLabelScale = 0.75;

/** Internal replacement for the legacy `<app-form-field>`; base structural styles live in `form-field.global.scss`. */
@Component({
  selector: 'app-form-field',
  exportAs: 'appFormField',
  templateUrl: './form-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  animations: [formFieldAnimations.transitionMessages],
  host: {
    class: 'app-form-field',
    '[class.app-form-field-appearance-outline]': "appearance === 'outline'",
    '[class.app-form-field-appearance-fill]': "appearance === 'fill'",
    '[class.app-form-field-label-above]': "labelPosition === 'above'",
    '[class.app-form-field-invalid]': '_control?.errorState',
    '[class.app-form-field-can-float]': '_canLabelFloat()',
    '[class.app-form-field-should-float]': '_shouldLabelFloat()',
    '[class.app-form-field-has-label]': '_hasFloatingLabel()',
    '[style.--app-form-field-placeholder-color]':
      "_hideControlPlaceholder() ? 'transparent' : null",
    '[class.app-form-field-disabled]': '_control?.disabled',
    '[class.app-focused]': '_control?.focused',
    '[class.ng-untouched]': "_shouldForward('untouched')",
    '[class.ng-touched]': "_shouldForward('touched')",
    '[class.ng-pristine]': "_shouldForward('pristine')",
    '[class.ng-dirty]': "_shouldForward('dirty')",
    '[class.ng-valid]': "_shouldForward('valid')",
    '[class.ng-invalid]': "_shouldForward('invalid')",
    '[class.ng-pending]': "_shouldForward('pending')",
    '[class._app-animation-noopable]': '!_animationsEnabled',
  },
})
export class FormFieldComponent
  implements AfterContentInit, AfterContentChecked, AfterViewInit, OnDestroy
{
  private _appearance: FormFieldAppearance = 'outline';
  @Input()
  get appearance(): FormFieldAppearance {
    return this._appearance;
  }
  set appearance(value: FormFieldAppearance) {
    const oldValue = this._appearance;
    this._appearance = value || 'outline';
    if (this._appearance === 'outline' && oldValue !== value) {
      this._outlineGapCalculationNeededOnStable = true;
    }
  }

  @Input() color: FormFieldColor = 'primary';

  private readonly _defaultLabelPosition: FormFieldLabelPosition;
  private _labelPosition: FormFieldLabelPosition;
  @Input()
  get labelPosition(): FormFieldLabelPosition {
    return this._labelPosition;
  }
  set labelPosition(value: FormFieldLabelPosition) {
    // Falls back to the injected default rather than to 'float': a template
    // that always binds the input would otherwise clobber the default with
    // undefined.
    this._labelPosition = value || this._defaultLabelPosition;
    this._outlineGapCalculationNeededOnStable = true;
  }

  private _hideRequiredMarker = false;
  @Input()
  get hideRequiredMarker(): boolean {
    return this._hideRequiredMarker;
  }
  set hideRequiredMarker(value: BooleanInput) {
    this._hideRequiredMarker = coerceBooleanProperty(value);
  }

  private _floatLabel: FloatLabelType = 'auto';
  @Input()
  get floatLabel(): FloatLabelType {
    return this._floatLabel === 'never' ? 'auto' : this._floatLabel;
  }
  set floatLabel(value: FloatLabelType) {
    if (value !== this._floatLabel) {
      this._floatLabel = value || 'auto';
      this._changeDetectorRef.markForCheck();
    }
  }

  readonly _labelId = `app-form-field-label-${nextUniqueId++}`;
  _subscriptAnimationState = '';
  readonly _animationsEnabled: boolean;

  private _outlineGapCalculationNeededImmediately = false;
  private _outlineGapCalculationNeededOnStable = false;
  private readonly _destroyed = new Subject<void>();

  @ContentChild(FormFieldControl)
  _controlNonStatic?: FormFieldControl<unknown>;
  @ContentChild(FormFieldControl, { static: true })
  _controlStatic?: FormFieldControl<unknown>;
  get _control(): FormFieldControl<unknown> {
    return (this._controlNonStatic ||
      this._controlStatic) as FormFieldControl<unknown>;
  }

  @ContentChild(FormFieldLabelDirective)
  _labelChildNonStatic?: FormFieldLabelDirective;
  @ContentChild(FormFieldLabelDirective, { static: true })
  _labelChildStatic?: FormFieldLabelDirective;

  @ContentChildren(FormFieldErrorDirective, { descendants: true })
  _errorChildren!: QueryList<FormFieldErrorDirective>;
  @ContentChildren(FormFieldHintDirective, { descendants: true })
  _hintChildren!: QueryList<FormFieldHintDirective>;
  @ContentChildren(FormFieldPrefixDirective, { descendants: true })
  _prefixChildren!: QueryList<FormFieldPrefixDirective>;
  @ContentChildren(FormFieldSuffixDirective, { descendants: true })
  _suffixChildren!: QueryList<FormFieldSuffixDirective>;

  @ViewChild('connectionContainer', { static: true })
  _connectionContainerRef!: ElementRef<HTMLElement>;
  @ViewChild('label') _label?: ElementRef<HTMLElement>;

  constructor(
    private _elementRef: ElementRef<HTMLElement>,
    private _changeDetectorRef: ChangeDetectorRef,
    private _platform: Platform,
    private _ngZone: NgZone,
    @Optional() private _dir: Directionality | null,
    @Optional() @Inject(ANIMATION_MODULE_TYPE) animationMode?: string,
    @Optional()
    @Inject(APP_FORM_FIELD_DEFAULT_OPTIONS)
    defaults?: AppFormFieldDefaultOptions | null
  ) {
    this._animationsEnabled = animationMode !== 'NoopAnimations';
    this._defaultLabelPosition = defaults?.labelPosition || 'float';
    this._labelPosition = this._defaultLabelPosition;
  }

  ngAfterContentInit(): void {
    this._validateControlChild();
    const control = this._control;
    if (control.controlType) {
      this._elementRef.nativeElement.classList.add(
        `app-form-field-type-${control.controlType}`
      );
    }
    control.stateChanges.pipe(startWith(null)).subscribe(() => {
      this._syncDescribedByIds();
      this._changeDetectorRef.markForCheck();
    });
    if (control.ngControl && control.ngControl.valueChanges) {
      control.ngControl.valueChanges
        .pipe(takeUntil(this._destroyed))
        .subscribe(() => this._changeDetectorRef.markForCheck());
    }
    // The appearance setter only arms the gap calc on a transition into
    // outline, which default-outline fields never hit; arm it here so the
    // notch is sized on first render.
    if (this.appearance === 'outline') {
      this._outlineGapCalculationNeededOnStable = true;
      this._recomputeOutlineGapOnFontLoad();
    }
    // Outside the zone so `zone-patch-rxjs` can't loop us on stabilization.
    this._ngZone.runOutsideAngular(() => {
      this._ngZone.onStable.pipe(takeUntil(this._destroyed)).subscribe(() => {
        if (this._outlineGapCalculationNeededOnStable) {
          this.updateOutlineGap();
        }
      });
    });
    merge(this._prefixChildren.changes, this._suffixChildren.changes).subscribe(
      () => {
        this._outlineGapCalculationNeededOnStable = true;
        this._changeDetectorRef.markForCheck();
      }
    );
    this._hintChildren.changes.pipe(startWith(null)).subscribe(() => {
      this._syncDescribedByIds();
      this._changeDetectorRef.markForCheck();
    });
    this._errorChildren.changes.pipe(startWith(null)).subscribe(() => {
      this._syncDescribedByIds();
      this._changeDetectorRef.markForCheck();
    });
    if (this._dir) {
      this._dir.change.pipe(takeUntil(this._destroyed)).subscribe(() => {
        if (typeof requestAnimationFrame === 'function') {
          this._ngZone.runOutsideAngular(() => {
            requestAnimationFrame(() => this.updateOutlineGap());
          });
        } else {
          this.updateOutlineGap();
        }
      });
    }
  }

  ngAfterContentChecked(): void {
    this._validateControlChild();
    if (this._outlineGapCalculationNeededImmediately) {
      this.updateOutlineGap();
    }
  }

  ngAfterViewInit(): void {
    // Avoid the subscript animation firing on initial load.
    this._subscriptAnimationState = 'enter';
    this._changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
  }

  _shouldForward(prop: NgControlStatus): boolean {
    const control = this._control ? this._control.ngControl : null;
    return !!(control && (control as Record<NgControlStatus, unknown>)[prop]);
  }

  _hasLabel(): boolean {
    return !!(this._labelChildNonStatic || this._labelChildStatic);
  }

  _isLabelAbove(): boolean {
    return this._labelPosition === 'above';
  }

  _canLabelFloat(): boolean {
    // A static label must not get the float classes: they are what turns the
    // outline gap's top border transparent to make room for the floating label.
    return !this._isLabelAbove() && this.floatLabel !== 'never';
  }

  _shouldAlwaysFloat(): boolean {
    return this.floatLabel === 'always';
  }

  _shouldLabelFloat(): boolean {
    return (
      this._canLabelFloat() &&
      ((this._control && this._control.shouldLabelFloat) ||
        this._shouldAlwaysFloat())
    );
  }

  _hideControlPlaceholder(): boolean {
    // A static label never covers the control, so the placeholder can show.
    return (
      !this._isLabelAbove() && this._hasLabel() && !this._shouldLabelFloat()
    );
  }

  _hasFloatingLabel(): boolean {
    return !this._isLabelAbove() && this._hasLabel();
  }

  _hasStaticLabel(): boolean {
    return this._isLabelAbove() && this._hasLabel();
  }

  _getDisplayedMessages(): 'error' | 'hint' {
    return this._errorChildren &&
      this._errorChildren.length > 0 &&
      this._control.errorState
      ? 'error'
      : 'hint';
  }

  private _syncDescribedByIds(): void {
    if (!this._control) {
      return;
    }
    const ids: string[] = [];
    if (
      this._control.userAriaDescribedBy &&
      typeof this._control.userAriaDescribedBy === 'string'
    ) {
      ids.push(...this._control.userAriaDescribedBy.split(' '));
    }
    if (this._getDisplayedMessages() === 'hint') {
      const startHint = this._hintChildren
        ? this._hintChildren.find((hint) => hint.align === 'start')
        : null;
      const endHint = this._hintChildren
        ? this._hintChildren.find((hint) => hint.align === 'end')
        : null;
      if (startHint) {
        ids.push(startHint.id);
      }
      if (endHint) {
        ids.push(endHint.id);
      }
    } else if (this._errorChildren) {
      ids.push(...this._errorChildren.map((error) => error.id));
    }
    this._control.setDescribedByIds(ids);
  }

  private _validateControlChild(): void {
    if (!this._control && isDevMode()) {
      throw Error('app-form-field must contain a FormFieldControl.');
    }
  }

  /** Sizes/positions the outline gap so the floating label notches into the border (outline only). */
  updateOutlineGap(): void {
    const labelEl = this._label ? this._label.nativeElement : null;
    const container = this._connectionContainerRef.nativeElement;
    const outlineStartSelector = '.app-form-field-outline-start';
    const outlineGapSelector = '.app-form-field-outline-gap';

    if (this.appearance !== 'outline' || !this._platform.isBrowser) {
      return;
    }

    // A static label sits above the control, so the outline needs no notch —
    // same geometry as having no label at all.
    if (
      this._isLabelAbove() ||
      !labelEl ||
      !labelEl.children.length ||
      !labelEl.textContent?.trim()
    ) {
      const gapElements = container.querySelectorAll<HTMLElement>(
        `${outlineStartSelector}, ${outlineGapSelector}`
      );
      for (let i = 0; i < gapElements.length; i++) {
        gapElements[i].style.width = '0';
      }
      return;
    }

    if (!this._isAttachedToDOM()) {
      this._outlineGapCalculationNeededImmediately = true;
      return;
    }

    let startWidth = 0;
    let gapWidth = 0;
    const startEls =
      container.querySelectorAll<HTMLElement>(outlineStartSelector);
    const gapEls = container.querySelectorAll<HTMLElement>(outlineGapSelector);
    if (this._label && this._label.nativeElement.children.length) {
      const containerRect = container.getBoundingClientRect();
      // Invisible (zero-size) container: defer to the next stable tick because
      // the ClientRect won't be right even once it becomes visible.
      if (containerRect.width === 0 && containerRect.height === 0) {
        this._outlineGapCalculationNeededOnStable = true;
        this._outlineGapCalculationNeededImmediately = false;
        return;
      }
      // `getBoundingClientRect()` returns transform-scaled viewport coords,
      // while `offsetWidth` (used for `gapWidth`) is layout-space and immune to
      // transforms. Under an ancestor `transform: scale()` — e.g. a dialog's
      // scale(0.7)->none enter animation — the two live in mismatched spaces and
      // the notch is under-sized, cutting the label. Normalise the rect distance
      // back to layout space by the container's current scale; for a uniform
      // scale this is exact at any animation frame. Treat ~1 as 1 to avoid
      // sub-pixel churn on untransformed fields.
      const rawScale =
        container.offsetWidth > 0
          ? containerRect.width / container.offsetWidth
          : 1;
      const scale =
        !isFinite(rawScale) || rawScale <= 0 || Math.abs(rawScale - 1) < 0.01
          ? 1
          : rawScale;
      const containerStart = this._getStartEnd(containerRect);
      const labelChildren = labelEl.children;
      const labelStart = this._getStartEnd(
        labelChildren[0].getBoundingClientRect()
      );
      let labelWidth = 0;
      for (let i = 0; i < labelChildren.length; i++) {
        labelWidth += (labelChildren[i] as HTMLElement).offsetWidth;
      }
      startWidth =
        Math.abs(labelStart - containerStart) / scale - outlineGapPadding;
      gapWidth =
        labelWidth > 0
          ? labelWidth * floatingLabelScale + outlineGapPadding * 2
          : 0;
    }

    for (let i = 0; i < startEls.length; i++) {
      startEls[i].style.width = `${startWidth}px`;
    }
    for (let i = 0; i < gapEls.length; i++) {
      gapEls[i].style.width = `${gapWidth}px`;
    }

    this._outlineGapCalculationNeededOnStable =
      this._outlineGapCalculationNeededImmediately = false;
  }

  private _getStartEnd(rect: { left: number; right: number }): number {
    return this._dir && this._dir.value === 'rtl' ? rect.right : rect.left;
  }

  private _isAttachedToDOM(): boolean {
    const element = this._elementRef.nativeElement;
    if (element.getRootNode) {
      const rootNode = element.getRootNode();
      return !!rootNode && rootNode !== element;
    }
    return document.documentElement.contains(element);
  }

  // `font-display: swap` paints the label in the fallback font, then reflows to
  // a different width once the web font loads — after the one-shot gap
  // measurement has run and cleared its flags. `cdkObserveContent` is a
  // MutationObserver and doesn't see font-driven size changes, so recompute
  // once fonts settle. `document.fonts` is absent on legacy browsers/SSR.
  private _recomputeOutlineGapOnFontLoad(): void {
    const fontSet = this._platform.isBrowser
      ? (document.fonts as FontFaceSet | undefined)
      : undefined;
    if (!fontSet) {
      return;
    }
    from(fontSet.ready)
      .pipe(takeUntil(this._destroyed))
      .subscribe(() =>
        this._ngZone.runOutsideAngular(() =>
          requestAnimationFrame(() => this.updateOutlineGap())
        )
      );
  }
}
